import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';

export type CustomerCompanyBranchMappingRow = {
  salesCompany: number | string;
  salesBranch: number | string;
  defaultMapping?: boolean;
};

type CustomerCompanyBranchMappingsProps = {
  mappings: CustomerCompanyBranchMappingRow[];
  companies: any[];
  branchesByCompany: Record<string, any[]>;
  disabledCompanyId?: number;
  disabledBranchId?: number;
  onChange: (mappings: CustomerCompanyBranchMappingRow[]) => void;
  onCompanyChange: (companyId: number | string) => void;
};

const emptyMapping = (): CustomerCompanyBranchMappingRow => ({
  salesCompany: '',
  salesBranch: '',
  defaultMapping: false,
});

const mappingKey = (mapping: CustomerCompanyBranchMappingRow) =>
  mapping.salesCompany && mapping.salesBranch ? `${mapping.salesCompany}-${mapping.salesBranch}` : '';

const uniqueMappings = (mappings: CustomerCompanyBranchMappingRow[]) => {
  const seen = new Set<string>();
  return mappings.filter(mapping => {
    const key = mappingKey(mapping);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const CustomerCompanyBranchMappings = ({
  mappings,
  companies,
  branchesByCompany,
  disabledCompanyId,
  disabledBranchId,
  onChange,
  onCompanyChange,
}: CustomerCompanyBranchMappingsProps) => {
  const rows = mappings.length > 0 ? mappings : [{ ...emptyMapping(), defaultMapping: true }];

  const updateRow = (index: number, patch: Partial<CustomerCompanyBranchMappingRow>) => {
    const next = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    onChange(uniqueMappings(next));
  };

  const addRow = () => {
    onChange([...rows, emptyMapping()]);
  };

  const removeRow = (index: number) => {
    const next = rows.filter((_, rowIndex) => rowIndex !== index);
    if (next.length > 0 && !next.some(row => row.defaultMapping)) {
      next[0] = { ...next[0], defaultMapping: true };
    }
    onChange(next);
  };

  const setDefault = (index: number) => {
    onChange(rows.map((row, rowIndex) => ({ ...row, defaultMapping: rowIndex === index })));
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Company Branch Mapping
      </Typography>
      <Grid container spacing={1.5}>
        {rows.map((row, index) => {
          const companyValue = row.salesCompany || '';
          const branchOptions = companyValue ? branchesByCompany[String(companyValue)] || [] : [];
          const branchRequired = !!companyValue && !row.salesBranch;
          const usedBranchesForCompany = rows
            .filter((mapping, rowIndex) => rowIndex !== index && String(mapping.salesCompany) === String(companyValue))
            .map(mapping => String(mapping.salesBranch))
            .filter(Boolean);
          const lockCurrentMapping =
            index === 0 &&
            !!disabledCompanyId &&
            !!disabledBranchId &&
            Number(row.salesCompany) === Number(disabledCompanyId) &&
            Number(row.salesBranch) === Number(disabledBranchId);

          return (
            <Grid item xs={12} key={`${index}-${row.salesCompany}-${row.salesBranch}`}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="medium">
                      <InputLabel>Company</InputLabel>
                      <Select
                        label="Company"
                        value={companyValue}
                        disabled={lockCurrentMapping}
                        onChange={event => {
                          const value = event.target.value;
                          onCompanyChange(value);
                          updateRow(index, { salesCompany: value, salesBranch: '' });
                        }}>
                        {companies.map((company: any) => (
                          <MenuItem key={company.value} value={company.value}>
                            {company.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="medium" error={branchRequired}>
                      <InputLabel>Branch{companyValue ? ' *' : ''}</InputLabel>
                      <Select
                        label={`Branch${companyValue ? ' *' : ''}`}
                        value={row.salesBranch || ''}
                        disabled={!companyValue || lockCurrentMapping}
                        onChange={event => updateRow(index, { salesBranch: event.target.value })}>
                        {branchOptions.map((branch: any) => (
                          <MenuItem
                            key={branch.value}
                            value={branch.value}
                            disabled={usedBranchesForCompany.includes(String(branch.value))}>
                            {branch.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8} md={3}>
                    <FormControlLabel
                      control={<Radio checked={!!row.defaultMapping} onChange={() => setDefault(index)} />}
                      label="Default"
                    />
                  </Grid>
                  <Grid item xs={4} md={1} textAlign="right">
                    <Tooltip title="Remove mapping">
                      <span>
                        <IconButton disabled={rows.length === 1 || lockCurrentMapping} onClick={() => removeRow(index)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Button startIcon={<AddIcon />} onClick={addRow}>
            Add New Company Mapping
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerCompanyBranchMappings;
